import {
  cn,
  NumberInput as HeroNumberInput,
  NumberInputProps as HeroNumberInputProps,
} from '@heroui/react'
import { motion } from 'framer-motion'

export interface NumberInputProps extends Omit<HeroNumberInputProps, 'classNames' | 'onChange'> {
  label?: string
  classNameContainer?: string
  variant?: 'bordered' | 'flat' | 'faded' | 'underlined'
  classNames?: {
    label?: string[]
    input?: string[]
    innerWrapper?: string
    inputWrapper?: string[]
  }
  labelPlacement?: 'outside' | 'inside' | 'outside-left'
  name?: string
  isRequired?: boolean
  value?: number
  onChange?: (value: number | undefined) => void
  onTypeChange?: (e: number) => void
  onBlur?: () => void
  errorMessage?: string
}

const NumberInput = ({
  label,
  classNameContainer,
  variant = 'bordered',
  classNames,
  labelPlacement = 'outside',
  name,
  isRequired = false,
  value,
  onChange,
  onTypeChange,
  onBlur,
  ...props
}: NumberInputProps) => {
  return (
    <motion.div className={cn(classNameContainer)}>
      <HeroNumberInput
        fullWidth
        className="h-full"
        isRequired={isRequired}
        label={label}
        labelPlacement={labelPlacement}
        name={name}
        radius="sm"
        value={value}
        variant={variant}
        classNames={{
          label: [
            'text-gray-900',
            'dark:text-foreground',
            'group-data-[filled-within=true]:text-gray-900',
            'dark:group-data-[filled-within=true]:text-white',
            'group-data-[invalid=true]:text-danger',
            'dark:group-data-[invalid=true]:text-danger',
            ...(classNames?.label || []),
          ],
          input: [
            'bg-transparent',
            'text-gray-900',
            'dark:text-white',
            'placeholder:text-gray-500',
            'dark:placeholder:text-white/60',
            'group-data-[invalid=true]:placeholder:text-danger',
            ...(classNames?.input || []),
          ],
          innerWrapper: 'bg-transparent',
          inputWrapper: [
            'bg-gray-50',
            'dark:bg-[#1e1e20]',
            'border-gray-200',
            'dark:border-default-600',
            'hover:bg-gray-100',
            'dark:hover:bg-[#1e1e20]/70',
            'group-data-[focus=true]:bg-gray-100',
            'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
            'group-data-[focus=true]:border-primary',
            'dark:group-data-[focus=true]:border-primary',
            '!cursor-text',
            ...(classNames?.inputWrapper || []),
          ],
          stepperButton: [
            '!h-4',
            '!w-6',
            'rounded-full',
            'hover:bg-gray-100',
            'dark:hover:bg-gray-700',
          ],
        }}
        onBlur={onBlur}
        onValueChange={onChange}
        onChange={e => {
          if (onTypeChange) {
            let rawValue: string

            if (typeof e === 'object' && e !== null && 'target' in e) {
              rawValue = e.target.value
            } else {
              rawValue = String(e)
            }

            // Strip formatting characters (commas, spaces, etc.) before parsing
            const cleanedValue = rawValue.replace(/[,\s]/g, '')

            // Skip if empty (allow user to clear the field)
            if (cleanedValue === '') {
              return
            }

            // Parse the cleaned value
            const numValue = Number(cleanedValue)

            // Only update if we have a valid number
            if (!isNaN(numValue)) {
              onTypeChange(numValue)
            }
          }
        }}
        {...props}
      />
    </motion.div>
  )
}

export default NumberInput
